import React from "react";
import styled from "styled-components";
import { ArrowLeft01Icon, ArrowRight01Icon } from "@hugeicons/core-free-icons";
import HugeIcon from "src/components/HugeIcon";
import { colors } from "src/theme/colors";
import { radii } from "src/theme/ui";

type Props = {
  collapse: boolean;
  setCollapse: (collapse) => void;
};

/**
 * The rail's collapse control, at the foot of the rail beside the name.
 *
 * It was the rail's own edge: a grab strip down the full height, invisible
 * until the pointer found it. That is what football does, and it asks the
 * reader to discover it — there was nothing on screen to say the rail could
 * open at all. A button next to the name says so, and sits where the rail's
 * other persistent control already is.
 *
 * Same shape as the tables' row-actions kebab — 28px, quiet until pointed at
 * — so the two glyph buttons in the chrome are the one control.
 *
 * The arrow points the way the rail will move: left to pull it closed, right
 * to push it open.
 */

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: ${radii.md}px;
  background: transparent;
  color: ${colors.gray[400]};
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: ${colors.gray[100]};
    color: ${colors.gray[700]};
  }

  &:focus-visible {
    outline: none;
    background: ${colors.gray[100]};
    color: ${colors.gray[700]};
    box-shadow: 0 0 0 2px ${colors.primary[200]};
  }
`;

const CollapseButton: React.FC<Props> = ({ collapse, setCollapse }) => (
  <Button
    type="button"
    aria-label={collapse ? "Expand sidebar" : "Collapse sidebar"}
    aria-expanded={!collapse}
    title={collapse ? "Expand sidebar" : "Collapse sidebar"}
    onClick={() => setCollapse(!collapse)}
  >
    <HugeIcon
      icon={collapse ? ArrowRight01Icon : ArrowLeft01Icon}
      size={16}
    />
  </Button>
);

export default CollapseButton;
